/** "Create new mandate" wizard — a replacement PayTo / BECS agreement raised from the AUS customer file (arrears, portfolio,
 *  legal). Reuses the funding-wizard plumbing; the mandate is linked to the case the moment the customer authorises it.
 *  Mandate only — the schedule is rebuilt afterwards with Create payment schedule / Revise schedule (Dean, 3 Nov 2026). */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Copy, Loader2, Mail, MessageSquare, Landmark, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/regionFmt";
import { gcNewMandateCreate, gcNewMandatePrefill, gcNewMandateSend, gcNewMandateStatus } from "@/lib/api";

const STEPS = ["Details", "Issue link", "Authorisation"];
const FREQ = [["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["daily", "Daily"], ["monthly", "Monthly"]];
const errMsg = (e) => e?.response?.data?.detail || e.message;

const Stepper = ({ step }) => (
  <ol className="flex items-center gap-1 text-[10px] uppercase tracking-wide" data-testid="gc-nm-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className="flex items-center gap-1">
        <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>{i < step ? "✓" : i + 1}</span>
        <span className={i === step ? "text-slate-900 font-semibold" : "text-slate-400"}>{s}</span>
        {i < STEPS.length - 1 && <span className="w-4 h-px bg-slate-300 mx-0.5" />}
      </li>
    ))}
  </ol>
);

const Field = ({ label, children, hint }) => (
  <label className="block text-xs">
    <span className="text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="block text-[10px] text-muted-foreground mt-0.5">{hint}</span>}
  </label>
);

function SchemePick({ value, onChange }) {
  const opt = (k, title, sub, cls) => (
    <button type="button" onClick={() => onChange(k)} data-testid={`gc-nm-scheme-${k}`}
            className={`flex-1 text-left border rounded-lg p-2.5 transition-colors ${value === k ? cls : "border-slate-200 hover:border-slate-300"}`}>
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-[11px] text-muted-foreground">{sub}</p>
    </button>
  );
  return (
    <div className="flex gap-2">
      {opt("pay_to", "PayTo", "Standard for weekly plans — real-time, agreement cap set now", "border-violet-500 bg-violet-50")}
      {opt("becs", "BECS Direct Debit", "Daily plans, or when the customer's bank doesn't support PayTo", "border-slate-800 bg-slate-50")}
    </div>
  );
}

function DetailsStep({ pre, form, set }) {
  const payto = form.scheme === "pay_to";
  const amt = parseFloat(form.instalment_amount || 0);
  const cap = payto && amt > 0 && pre.payto_policy?.multiplier ? amt * pre.payto_policy.multiplier : null;
  return (
    <div className="space-y-3" data-testid="gc-nm-details">
      {pre.live_mandates?.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-2 text-xs text-amber-900 flex gap-2" data-testid="gc-nm-live-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>This account already has a live mandate ({pre.live_mandates.map((m) => `${m.scheme_label} ${m.mandate_id.slice(-6)}`).join(", ")}). The old one keeps collecting until you cancel its schedule — once the customer authorises the new one it becomes the working mandate on this tab.</span>
        </div>
      )}
      <SchemePick value={form.scheme} onChange={(v) => set({ scheme: v })} />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Payer given name"><Input value={form.given_name} onChange={(e) => set({ given_name: e.target.value })} className="h-8 text-sm" data-testid="gc-nm-given" /></Field>
        <Field label="Payer family name"><Input value={form.family_name} onChange={(e) => set({ family_name: e.target.value })} className="h-8 text-sm" data-testid="gc-nm-family" /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="h-8 text-sm" data-testid="gc-nm-email" /></Field>
        <Field label="Mobile"><Input value={form.mobile} onChange={(e) => set({ mobile: e.target.value })} placeholder="04xx xxx xxx" className="h-8 text-sm" data-testid="gc-nm-mobile" /></Field>
      </div>
      <p className="text-[11px] text-muted-foreground">Business: <span className="font-medium text-slate-800">{pre.business_name}</span> · {pre.sfa_number}. The customer enters their bank details on GoCardless's secure page.</p>
      {payto && (
        <div className="border rounded-lg p-2.5 bg-violet-50/50 space-y-2" data-testid="gc-nm-payto-box">
          <p className="text-xs font-semibold text-violet-900">PayTo agreement limits — set now, can't be raised later</p>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Instalment per payment (AUD)" hint={pre.instalment ? `From ${pre.instalment.source}` : "No live schedule found — enter the agreed instalment"}>
              <Input type="number" min="1" step="0.01" value={form.instalment_amount} onChange={(e) => set({ instalment_amount: e.target.value })} className="h-8 text-sm" data-testid="gc-nm-instalment" />
            </Field>
            <Field label="Frequency">
              <select value={form.instalment_frequency} onChange={(e) => set({ instalment_frequency: e.target.value })} className="h-8 w-full text-sm border rounded px-2 bg-white" data-testid="gc-nm-frequency">
                {FREQ.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>
          <p className="text-[11px] text-violet-900" data-testid="gc-nm-cap-preview">
            {cap ? <>Cap per payment <b>{fmtMoney(cap, 2)}</b> ({pre.payto_policy.multiplier}× the instalment) · up to <b>{pre.payto_policy.max_payments_per_week}</b> payments a week — room for daily arrears catch-ups.</> : "Enter the instalment to see the agreement cap."}
          </p>
        </div>
      )}
      <Field label="Reason (optional — goes on the case notes)"><Input value={form.reason} onChange={(e) => set({ reason: e.target.value })} placeholder="e.g. customer changed banks / PayTo agreement cancelled by payer" className="h-8 text-sm" data-testid="gc-nm-reason" /></Field>
    </div>
  );
}

function IssueStep({ caseId, created, form, onSent }) {
  const [channel, setChannel] = useState(form.email ? "email" : form.mobile ? "sms" : "copy");
  const [to, setTo] = useState(channel === "sms" ? form.mobile : form.email);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState([]);
  const pick = (c) => { setChannel(c); setTo(c === "sms" ? form.mobile : c === "email" ? form.email : ""); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(created.authorisation_url); } catch { /* fall through — link is visible below */ }
    toast.success("Authorisation link copied");
    try { const r = await gcNewMandateSend(caseId, created.billing_request_id, { channel: "copy" }); setDone((d) => [...d, r.send]); onSent(); } catch { /* noted best-effort */ }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await gcNewMandateSend(caseId, created.billing_request_id, { channel, to });
      setDone((d) => [...d, r.send]);
      toast.success(channel === "sms" ? `SMS sent to ${r.to || to}` : `Email sent to ${to}`);
      onSent();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const tab = (k, Icon, label) => (
    <button type="button" onClick={() => pick(k)} data-testid={`gc-nm-channel-${k}`}
            className={`flex-1 border rounded-lg p-2 text-xs font-medium flex items-center justify-center gap-1.5 ${channel === k ? "border-slate-900 bg-slate-900 text-white" : "hover:bg-slate-50"}`}>
      <Icon className="w-3.5 h-3.5" />{label}
    </button>
  );
  return (
    <div className="space-y-3" data-testid="gc-nm-issue">
      <div className="border rounded-lg p-2.5 bg-emerald-50 text-xs text-emerald-900 flex gap-2">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span>{created.scheme === "becs" ? "BECS Direct Debit" : "PayTo"} invite <span className="font-mono">{created.billing_request_id}</span> created in GoCardless. Now get the authorisation link to the customer.</span>
      </div>
      <div className="flex gap-2">{tab("email", Mail, "Email")}{tab("sms", MessageSquare, "SMS")}{tab("copy", Copy, "Copy link")}</div>
      {channel !== "copy" ? (
        <div className="flex gap-2 items-end">
          <Field label={channel === "sms" ? "Mobile" : "Email"}><Input value={to} onChange={(e) => setTo(e.target.value)} className="h-8 text-sm w-64" data-testid="gc-nm-send-to" /></Field>
          <Button size="sm" className="h-8" onClick={send} disabled={busy || !to.trim()} data-testid="gc-nm-send-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : channel === "sms" ? "Send SMS" : "Send email"}</Button>
        </div>
      ) : (
        <Button size="sm" className="h-8" onClick={copy} data-testid="gc-nm-copy-btn"><Copy className="w-3.5 h-3.5 mr-1" />Copy authorisation link</Button>
      )}
      <p className="text-[10px] text-muted-foreground break-all">Link: <a href={created.authorisation_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline" data-testid="gc-nm-auth-link">{created.authorisation_url}</a></p>
      {done.length > 0 && <ul className="text-[11px] text-slate-700 space-y-0.5" data-testid="gc-nm-sent-list">{done.map((s, i) => <li key={i}>✓ {s.channel === "copy" ? "Link copied" : `${s.channel === "sms" ? "SMS" : "Email"} → ${s.to}`}</li>)}</ul>}
    </div>
  );
}

function WaitStep({ caseId, created }) {
  const [state, setState] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const s = await gcNewMandateStatus(caseId); if (alive) setState(s); } catch { /* keep polling */ }
      if (alive) timer.current = setTimeout(tick, 10000);
    };
    tick();
    return () => { alive = false; clearTimeout(timer.current); };
  }, [caseId]);
  const mine = state?.authorised_mandates?.[0];
  const stillOpen = state?.pending_invites?.some((i) => i.billing_request_id === created.billing_request_id);
  return (
    <div className="space-y-3 text-sm" data-testid="gc-nm-wait">
      {mine ? (
        <div className="border border-emerald-300 bg-emerald-50 rounded-lg p-3 text-emerald-900 flex gap-2" data-testid="gc-nm-authorised">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <div>
            <p className="font-semibold">Authorised — {mine.scheme_label} mandate <span className="font-mono">{mine.mandate_id}</span> is live.</p>
            <p className="text-xs mt-1">It's now the working mandate on this account. Next: rebuild the repayment schedule on it with <b>Create payment schedule</b>, then cancel the old schedule.</p>
            <a href={mine.dashboard_url} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-0.5 mt-1">Open in GoCardless<ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-3 flex gap-2 text-slate-700" data-testid="gc-nm-waiting">
          <Loader2 className="w-4 h-4 animate-spin shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Waiting for the customer to authorise…</p>
            <p className="text-xs text-muted-foreground mt-1">{stillOpen === false && state ? "This invite is no longer open in GoCardless." : "This checks GoCardless every 10 seconds. You can close this — the invite stays on the GoCardless tab with resend / copy / cancel until it's authorised, and the mandate links itself to the account the moment it is."}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NewMandateWizard({ caseId, onClose, onDone }) {
  const [pre, setPre] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ scheme: "pay_to", given_name: "", family_name: "", email: "", mobile: "", instalment_amount: "", instalment_frequency: "weekly", reason: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    gcNewMandatePrefill(caseId).then((p) => {
      setPre(p);
      setForm((f) => ({ ...f, scheme: p.default_scheme || "pay_to", given_name: p.given_name || "", family_name: p.family_name || "", email: p.email || "", mobile: p.mobile || "",
        instalment_amount: p.instalment?.amount ? String(p.instalment.amount) : "", instalment_frequency: p.instalment?.frequency || "weekly" }));
    }).catch((e) => toast.error(errMsg(e)));
  }, [caseId]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await gcNewMandateCreate(caseId, { ...form, instalment_amount: form.instalment_amount ? parseFloat(form.instalment_amount) : null });
      setCreated(r); setStep(1); onDone(false);
      toast.success("Mandate invite created in GoCardless");
    } catch (e) {
      const d = String(errMsg(e) || "");
      if (form.scheme === "pay_to" && /payto|pay_to|currency_doesnt_support/i.test(d)) { set({ scheme: "becs" }); toast.warning("PayTo isn't available for this customer — switched to BECS Direct Debit. Click Create again."); }
      else toast.error(d || "Could not create the mandate invite");
    } finally { setBusy(false); }
  };
  const canCreate = pre?.can_write && form.given_name.trim() && (form.scheme !== "pay_to" || parseFloat(form.instalment_amount || 0) > 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="gc-new-mandate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Landmark className="w-4 h-4" />Create new mandate</DialogTitle>
          <Stepper step={step} />
        </DialogHeader>
        {!pre ? <div className="py-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Looking up the customer…</div> : (
          <>
            {step === 0 && <DetailsStep pre={pre} form={form} set={set} />}
            {step === 1 && created && <IssueStep caseId={caseId} created={created} form={form} onSent={() => onDone(false)} />}
            {step === 2 && created && <WaitStep caseId={caseId} created={created} />}
            <div className="flex justify-between items-center pt-1">
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="gc-nm-close">Close</Button>
              {step === 0 && <Button size="sm" onClick={create} disabled={!canCreate || busy} data-testid="gc-nm-create">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Create {form.scheme === "pay_to" ? "PayTo" : "Direct Debit"} invite</Button>}
              {step === 1 && <Button size="sm" onClick={() => setStep(2)} data-testid="gc-nm-next-wait">Wait for authorisation</Button>}
              {step === 2 && <Button size="sm" onClick={() => onDone(true)} data-testid="gc-nm-done">Done</Button>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
